import { useState, useRef, useEffect } from 'react';
import { db } from '../firebase';
import { ref, push } from "firebase/database";
import emailjs from '@emailjs/browser';
import '../css/QuoteForm.css';
import appleLogo from '../pics/apple.png';
import samsungLogo from '../pics/samsung.png';

/* ─────────────────────────────────────────────────────────────
   EmailJS Config — fill these in after creating your account:
   1. Go to https://www.emailjs.com and sign up (free)
   2. Add an Email Service (Gmail works great) → copy Service ID
   3. Create an Email Template → copy Template ID
   4. Go to Account → API Keys → copy your Public Key
   ───────────────────────────────────────────────────────────── */
const EJS_SERVICE  = 'service_8jru0vd';   // e.g. 'service_abc123'
const EJS_TEMPLATE = 'template_ymv0x2h';  // e.g. 'template_xyz789'
const EJS_KEY      = 'aXPYSotU6w0p3hbJd';   // e.g. 'AbCdEfGhIjKlMnOp'

/* Template variables your EmailJS template should use:
   {{customer_name}}  {{customer_phone}}  {{customer_email}}
   {{device}}  {{repair}}  {{appt_date}}  {{appt_time}}
   {{location}} = 8239 Troy Pike, Huber Heights, OH
*/
const EJS_READY = EJS_SERVICE !== 'YOUR_SERVICE_ID';

/* ── SVG Icons ──────────────────────────────────────────────── */
const svgProps = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };

const PhoneCallIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgProps}>
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.11 7.3a16 16 0 006.59 6.59l1.36-1.14a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14.92z"/>
  </svg>
);

const CheckIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const ArrowRight = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgProps}>
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

/* ── Device Illustrations (inline SVGs) ─────────────────────── */
const IPhoneIllustration = () => (
  <svg width="88" height="88" viewBox="0 0 72 72" fill="none">
    <rect x="18" y="6" width="36" height="60" rx="7" fill="#1d1d1f" />
    <rect x="20" y="14" width="32" height="40" rx="2" fill="#3a86ff" opacity="0.85"/>
    <rect x="29" y="8" width="14" height="4" rx="2" fill="#3a3a3c"/>
    <circle cx="36" cy="59" r="3" fill="#3a3a3c"/>
    {/* screen shine */}
    <path d="M24 18 Q36 16 44 22" stroke="white" strokeWidth="1" opacity="0.3" strokeLinecap="round"/>
  </svg>
);

const SamsungIllustration = () => (
  <svg width="88" height="88" viewBox="0 0 72 72" fill="none">
    <rect x="17" y="5" width="38" height="62" rx="6" fill="#1428A0" />
    <rect x="19" y="13" width="34" height="44" rx="2" fill="#4db6ff" opacity="0.85"/>
    <rect x="29" y="7" width="14" height="3" rx="1.5" fill="#0d1f7a"/>
    <circle cx="36" cy="62" r="2.5" fill="#0d1f7a"/>
    <path d="M23 17 Q36 14 45 21" stroke="white" strokeWidth="1" opacity="0.3" strokeLinecap="round"/>
  </svg>
);

const IPadIllustration = () => (
  <svg width="88" height="88" viewBox="0 0 72 72" fill="none">
    <rect x="10" y="9" width="52" height="54" rx="6" fill="#1d1d1f" />
    <rect x="14" y="14" width="44" height="44" rx="2" fill="#34c8e8" opacity="0.85"/>
    <circle cx="36" cy="11" r="2" fill="#3a3a3c"/>
    <path d="M18 18 Q36 15 50 22" stroke="white" strokeWidth="1" opacity="0.3" strokeLinecap="round"/>
  </svg>
);

const MotorolaIllustration = () => (
  <svg width="88" height="88" viewBox="0 0 72 72" fill="none">
    <rect x="20" y="6" width="32" height="60" rx="7" fill="#2D2D2D" />
    <rect x="22" y="14" width="28" height="38" rx="2" fill="#e63946" opacity="0.85"/>
    {/* Motorola M logo */}
    <path d="M28 38 L30 28 L36 34 L42 28 L44 38" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <circle cx="36" cy="58" r="2.5" fill="#444"/>
    <rect x="30" y="8" width="12" height="3" rx="1.5" fill="#444"/>
  </svg>
);

const OtherIllustration = () => (
  <svg width="88" height="88" viewBox="0 0 72 72" fill="none">
    <rect x="20" y="6" width="32" height="60" rx="7" fill="#6e6e73" />
    <rect x="22" y="14" width="28" height="38" rx="2" fill="#aeaeb2" opacity="0.85"/>
    {/* ? mark */}
    <text x="36" y="37" textAnchor="middle" fill="white" fontSize="20" fontWeight="bold" fontFamily="sans-serif">?</text>
    <circle cx="36" cy="58" r="2.5" fill="#555"/>
    <rect x="30" y="8" width="12" height="3" rx="1.5" fill="#555"/>
  </svg>
);


/* ── Repair Icons ───────────────────────────────────────────── */
const ScreenSvg = () => (<svg width="28" height="28" viewBox="0 0 24 24" {...svgProps}><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M9 17h6"/></svg>);
const BatterySvg = () => (<svg width="28" height="28" viewBox="0 0 24 24" {...svgProps}><rect x="2" y="7" width="16" height="10" rx="2"/><path d="M22 11v2"/><path d="M7 12h4M9 10v4"/></svg>);
const ChargeSvg = () => (<svg width="28" height="28" viewBox="0 0 24 24" {...svgProps}><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>);
const CameraSvg = () => (<svg width="28" height="28" viewBox="0 0 24 24" {...svgProps}><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>);
const WaterSvg = () => (<svg width="28" height="28" viewBox="0 0 24 24" {...svgProps}><path d="M12 2c0 0-7 9-7 13a7 7 0 0014 0c0-4-7-13-7-13z"/></svg>);
const GlassSvg = () => (<svg width="28" height="28" viewBox="0 0 24 24" {...svgProps}><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 9h18M9 21V9"/></svg>);
const SpeakerSvg = () => (<svg width="28" height="28" viewBox="0 0 24 24" {...svgProps}><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M15.54 8.46a5 5 0 010 7.07"/></svg>);
const HomeSvg = () => (<svg width="28" height="28" viewBox="0 0 24 24" {...svgProps}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/></svg>);
const WrenchSvg = () => (<svg width="28" height="28" viewBox="0 0 24 24" {...svgProps}><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>);
const CalSvg   = () => (<svg width="20" height="20" viewBox="0 0 24 24" {...svgProps}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>);
const EmailSvg = () => (<svg width="20" height="20" viewBox="0 0 24 24" {...svgProps}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>);


/* ── Data ───────────────────────────────────────────────────── */
const devices = [
  { id: 'iPhone',       label: 'iPhone',       illustration: <IPhoneIllustration />,   hasModels: true },
  { id: 'Samsung',      label: 'Samsung',      illustration: <SamsungIllustration />,  hasModels: true },
  { id: 'iPad',         label: 'iPad',         illustration: <IPadIllustration />,     hasModels: true },
  { id: 'Motorola',     label: 'Motorola',     illustration: <MotorolaIllustration />, hasModels: false },
  { id: 'Other Device', label: 'Other Device', illustration: <OtherIllustration />,    hasModels: false },
];

const models = {
  iPhone: [
    'iPhone 17 Pro Max', 'iPhone 17 Pro', 'iPhone 17 Plus', 'iPhone 17',
    'iPhone 16 Pro Max', 'iPhone 16 Pro', 'iPhone 16 Plus', 'iPhone 16',
    'iPhone 15 Pro Max', 'iPhone 15 Pro', 'iPhone 15 Plus', 'iPhone 15',
    'iPhone 14 Pro Max', 'iPhone 14 Pro', 'iPhone 14 Plus', 'iPhone 14',
    'iPhone 13 Pro Max', 'iPhone 13 Pro', 'iPhone 13', 'iPhone 13 Mini',
    'iPhone 12 Pro Max', 'iPhone 12 Pro', 'iPhone 12', 'iPhone 12 Mini',
    'iPhone 11 Pro Max', 'iPhone 11 Pro', 'iPhone 11',
    'iPhone XS Max', 'iPhone XS', 'iPhone XR', 'iPhone X',
    'iPhone SE (3rd Gen)', 'iPhone SE (2nd Gen)', 'iPhone SE (1st Gen)',
    'iPhone 8 Plus', 'iPhone 8', 'iPhone 7 Plus', 'iPhone 7',
    'iPhone 6s Plus', 'iPhone 6s', 'iPhone 6 Plus', 'iPhone 6',
  ],
  Samsung: [
    'Galaxy S26 Ultra', 'Galaxy S26+', 'Galaxy S26',
    'Galaxy S25 Ultra', 'Galaxy S25+', 'Galaxy S25',
    'Galaxy S24 Ultra', 'Galaxy S24+', 'Galaxy S24',
    'Galaxy S23 Ultra', 'Galaxy S23+', 'Galaxy S23',
    'Galaxy S22 Ultra', 'Galaxy S22+', 'Galaxy S22',
    'Galaxy S21 Ultra', 'Galaxy S21+', 'Galaxy S21',
    'Galaxy S20 Ultra', 'Galaxy S20+', 'Galaxy S20',
    'Galaxy S10+', 'Galaxy S10', 'Galaxy S10e',
    'Galaxy S9+', 'Galaxy S9',
    'Galaxy S8+', 'Galaxy S8',
    'Galaxy Note 20 Ultra', 'Galaxy Note 20',
    'Galaxy Note 10+', 'Galaxy Note 10',
    'Galaxy Note 9', 'Galaxy Note 8',
    'Galaxy Z Fold 6', 'Galaxy Z Fold 5', 'Galaxy Z Fold 4', 'Galaxy Z Fold 3',
    'Galaxy Z Flip 6', 'Galaxy Z Flip 5', 'Galaxy Z Flip 4', 'Galaxy Z Flip 3',
    'Galaxy A73', 'Galaxy A54', 'Galaxy A53', 'Galaxy A52',
    'Galaxy A34', 'Galaxy A33', 'Galaxy A32',
    'Galaxy A25', 'Galaxy A24', 'Galaxy A23',
    'Galaxy A15', 'Galaxy A14', 'Galaxy A13', 'Galaxy A12',
  ],
  iPad: [
    'iPad Pro 13" (M4)', 'iPad Pro 11" (M4)',
    'iPad Pro 13" (M2)', 'iPad Pro 11" (M2)',
    'iPad Pro 12.9" (5th Gen)', 'iPad Pro 11" (3rd Gen)',
    'iPad Pro 12.9" (4th Gen)', 'iPad Pro 11" (2nd Gen)',
    'iPad Pro 12.9" (3rd Gen)', 'iPad Pro 11" (1st Gen)',
    'iPad Pro 12.9" (2nd Gen)', 'iPad Pro 12.9" (1st Gen)',
    'iPad Pro 10.5"', 'iPad Pro 9.7"',
    'iPad Air 13" (M2)', 'iPad Air 11" (M2)',
    'iPad Air (5th Gen)', 'iPad Air (4th Gen)',
    'iPad Air (3rd Gen)', 'iPad Air 2', 'iPad Air (1st Gen)',
    'iPad Mini (7th Gen)', 'iPad Mini (6th Gen)', 'iPad Mini (5th Gen)',
    'iPad Mini (4th Gen)', 'iPad Mini (3rd Gen)',
    'iPad (11th Gen)', 'iPad (10th Gen)', 'iPad (9th Gen)',
    'iPad (8th Gen)', 'iPad (7th Gen)', 'iPad (6th Gen)',
    'iPad (5th Gen)', 'iPad (4th Gen)',
  ],
};

const repairs = {
  iPhone: [
    { name: 'Screen Repair',        icon: <ScreenSvg /> },
    { name: 'Battery Replacement',  icon: <BatterySvg /> },
    { name: 'Charging Port Repair', icon: <ChargeSvg /> },
    { name: 'Back Glass Repair',    icon: <GlassSvg /> },
    { name: 'Camera Repair',        icon: <CameraSvg /> },
    { name: 'Water Damage Repair',  icon: <WaterSvg /> },
    { name: 'Speaker / Mic Repair', icon: <SpeakerSvg /> },
    { name: 'Home Button Repair',   icon: <HomeSvg /> },
    { name: 'Other / Not Sure',     icon: <WrenchSvg /> },
  ],
  Samsung: [
    { name: 'Screen Repair',        icon: <ScreenSvg /> },
    { name: 'Battery Replacement',  icon: <BatterySvg /> },
    { name: 'Charging Port Repair', icon: <ChargeSvg /> },
    { name: 'Back Glass Repair',    icon: <GlassSvg /> },
    { name: 'Camera Repair',        icon: <CameraSvg /> },
    { name: 'Water Damage Repair',  icon: <WaterSvg /> },
    { name: 'Speaker / Mic Repair', icon: <SpeakerSvg /> },
    { name: 'Other / Not Sure',     icon: <WrenchSvg /> },
  ],
  iPad: [
    { name: 'Screen Repair',        icon: <ScreenSvg /> },
    { name: 'Battery Replacement',  icon: <BatterySvg /> },
    { name: 'Charging Port Repair', icon: <ChargeSvg /> },
    { name: 'Camera Repair',        icon: <CameraSvg /> },
    { name: 'Water Damage Repair',  icon: <WaterSvg /> },
    { name: 'Other / Not Sure',     icon: <WrenchSvg /> },
  ],
  Motorola: [
    { name: 'Screen Repair',        icon: <ScreenSvg /> },
    { name: 'Battery Replacement',  icon: <BatterySvg /> },
    { name: 'Charging Port Repair', icon: <ChargeSvg /> },
    { name: 'Camera Repair',        icon: <CameraSvg /> },
    { name: 'Water Damage Repair',  icon: <WaterSvg /> },
    { name: 'Speaker / Mic Repair', icon: <SpeakerSvg /> },
    { name: 'Other / Not Sure',     icon: <WrenchSvg /> },
  ],
  'Other Device': [
    { name: 'Screen Repair',        icon: <ScreenSvg /> },
    { name: 'Battery Replacement',  icon: <BatterySvg /> },
    { name: 'Charging Port Repair', icon: <ChargeSvg /> },
    { name: 'Camera Repair',        icon: <CameraSvg /> },
    { name: 'Water Damage Repair',  icon: <WaterSvg /> },
    { name: 'Speaker / Mic Repair', icon: <SpeakerSvg /> },
    { name: 'Other / Not Sure',     icon: <WrenchSvg /> },
  ],
};

/* hours in 24h form; displayed as 12h */
const WEEKDAY_HOURS = [10,11,12,13,14,15,16,17,18];
const SUNDAY_HOURS  = [12,13,14,15];
const ALL_MINUTES   = Array.from({ length: 60 }, (_, i) => i);

function fmt12(h24) {
  const p = h24 >= 12 ? 'PM' : 'AM';
  const h = h24 > 12 ? h24 - 12 : h24;
  return `${h} ${p}`;
}
function fmtTime(h24, m) {
  const p = h24 >= 12 ? 'PM' : 'AM';
  const h = h24 > 12 ? h24 - 12 : h24;
  return `${h}:${String(m).padStart(2,'0')} ${p}`;
}

function getDayOfWeek(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

function formatDate(str) {
  if (!str) return '';
  const [y, m, d] = str.split('-');
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return `${months[parseInt(m,10)-1]} ${parseInt(d,10)}, ${y}`;
}

/* ── Steps ──────────────────────────────────────────────────── */
// 0 = Device, 1 = Model (skip if no models), 2 = Repair, 3 = Date, 4 = Done
const STEP_DEVICE = 0;
const STEP_MODEL  = 1;
const STEP_REPAIR = 2;
const STEP_DATE   = 3;
const STEP_DONE   = 4;

const stepLabels = ['Device', 'Model', 'Repair', 'Date', 'Done'];

/* ── Scroll-wheel drum picker ────────────────────── */
const ITEM_H     = 48;   // px per row
const VISIBLE    = 5;    // rows visible at once
const PAD        = Math.floor(VISIBLE / 2); // 2 rows of padding top/bottom

function ScrollPicker({ items, value, onChange, disabled, formatItem }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || items.length === 0 || disabled) return;
    const idx = (value !== null && value !== undefined) ? items.indexOf(value) : -1;
    if (idx < 0) {
      onChange(items[0]);
      ref.current.scrollTop = 0;
    } else {
      ref.current.scrollTop = idx * ITEM_H;
    }
  }, []); // runs once per mount; key prop drives re-mounts

  const handleScroll = () => {
    if (!ref.current || disabled) return;
    const idx = Math.round(ref.current.scrollTop / ITEM_H);
    const clamped = Math.max(0, Math.min(idx, items.length - 1));
    if (items[clamped] !== undefined && items[clamped] !== value) {
      onChange(items[clamped]);
    }
  };

  return (
    <div className={`qf__sp-wrap${disabled ? ' qf__sp-wrap--disabled' : ''}`}>
      <div className="qf__sp-highlight" />
      <div ref={ref} className="qf__sp" onScroll={handleScroll}>
        <div style={{ height: PAD * ITEM_H }} />
        {items.map((item, i) => (
          <div
            key={item}
            className={`qf__sp-item${item === value ? ' qf__sp-item--sel' : ''}`}
            onClick={() => {
              if (disabled || !ref.current) return;
              onChange(item);
              ref.current.scrollTop = i * ITEM_H;
            }}
          >
            {formatItem ? formatItem(item) : item}
          </div>
        ))}
        <div style={{ height: PAD * ITEM_H }} />
      </div>
    </div>
  );
}

export default function QuoteForm() {
  const [step,      setStep]     = useState(STEP_DEVICE);
  const [deviceId,  setDeviceId] = useState('');
  const [model,     setModel]    = useState('');
  const [repair,    setRepair]   = useState('');
  const [date,      setDate]     = useState('');
  const [selHour,   setSelHour]  = useState(null);
  const [selMin,    setSelMin]   = useState(null);
  const [custName,  setCustName] = useState('');
  const [custPhone, setCustPhone]= useState('');
  const [custEmail, setCustEmail]= useState('');
  const [sending,   setSending]  = useState(false);
  const [emailSent, setEmailSent]= useState(false);

  const timeSlot = selHour !== null && selMin !== null ? fmtTime(selHour, selMin) : '';

  const device = devices.find(d => d.id === deviceId);
  const today  = new Date().toISOString().split('T')[0];

  const isSunday   = getDayOfWeek(date) === 0;
  const isFriday   = getDayOfWeek(date) === 5;
  const hoursNote  = isSunday ? 'Sunday hours: 12 PM – 4 PM' : 'Hours: Mon – Sat  10 AM – 7 PM';
  const hours24    = isSunday ? SUNDAY_HOURS : WEEKDAY_HOURS;

  /* Block past slots when today is selected (with 30-min buffer) */
  const now = new Date();
  const isToday = date === today;
  const nowMins = isToday ? now.getHours() * 60 + now.getMinutes() + 30 : 0;

  /* Minutes available for a given hour — applies today-filter + business rules */
  const getMinsForHour = (h) => {
    let mins = isToday
      ? ALL_MINUTES.filter(m => h * 60 + m > nowMins)
      : [...ALL_MINUTES];
    if (!isSunday) {
      if (h === 10) mins = mins.filter(m => m >= 30); // 10:00–10:29 closed
      if (h === 18) mins = mins.filter(m => m < 15);  // 6:15 PM+ closed
    } else {
      if (h === 12) mins = mins.filter(m => m >= 30); // 12:00–12:29 closed (Sun)
      if (h === 15) mins = mins.filter(m => m < 30);  // 3:30 PM+ closed (Sun)
    }
    return mins;
  };

  const availHours = (() => {
    let hrs = isToday ? hours24.filter(h => h * 60 + 59 > nowMins) : [...hours24];
    if (isFriday) hrs = hrs.filter(h => h !== 13 && h !== 14); // 1–3 PM closed Fridays
    hrs = hrs.filter(h => getMinsForHour(h).length > 0);        // drop hours with no mins
    return hrs;
  })();
  const availMins = selHour !== null ? getMinsForHour(selHour) : ALL_MINUTES;

  /* Slots that need a call to confirm */
  const dayLastHour = hours24[hours24.length - 1];
  const needsCallConfirm = selHour !== null && (
    selHour === 10 ||          // 10–11 AM any day
    selHour === dayLastHour    // last hour of business any day
  );

  const reset = () => {
    setStep(STEP_DEVICE); setDeviceId(''); setModel(''); setRepair('');
    setDate(''); setSelHour(null); setSelMin(null);
    setCustName(''); setCustPhone(''); setCustEmail('');
    setSending(false); setEmailSent(false);
  };

  const selectAndAdvanceDevice = (d) => {
    setDeviceId(d.id);
    setModel('');
    setRepair('');
    if (d.hasModels) {
      setStep(STEP_MODEL);
    } else {
      setModel(d.id);
      setStep(STEP_REPAIR);
    }
  };

  const handleConfirm = async () => {
    setSending(true);
    try {
      await push(ref(db, 'appointments'), {
        name: custName,
        phone: custPhone,
        email: custEmail,
        device: displayDevice,
        repair: repair,
        date: date,
        time: timeSlot,
        createdAt: new Date().toISOString()
      });

      if (EJS_READY) {
        await emailjs.send(EJS_SERVICE, EJS_TEMPLATE, {
          to_email:        custEmail,
          customer_name:   custName,
          customer_phone:  custPhone,
          customer_email:  custEmail,
          device:          displayDevice,
          repair:          repair,
          appt_date:       formatDate(date),
          appt_time:       timeSlot,
          location:        '8239 Troy Pike, Huber Heights, OH 45424',
        }, EJS_KEY);
      }

      setEmailSent(EJS_READY);
    } catch (err) {
      console.error('Confirmation error:', err);
      setEmailSent(false);
    } finally {
      setSending(false);
      setStep(STEP_DONE);
    }
  };

  /* visual stepper: only show relevant steps */
  const visibleSteps = device?.hasModels
    ? ['Device', 'Model', 'Repair', 'Date', 'Done']
    : ['Device', 'Repair', 'Date', 'Done'];

  /* map internal step → visual index */
  const visualStep = device?.hasModels
    ? step
    : step === STEP_REPAIR ? 1
    : step === STEP_DATE   ? 2
    : step === STEP_DONE   ? 3
    : 0;

  const displayDevice = deviceId && model && model !== deviceId
    ? `${deviceId} ${model}`
    : deviceId;

  return (
    <section className="qf" id="quote">
      <div className="qf__outer">

        {/* ── Page Header ── */}
        <div className="qf__page-header">
          <span className="qf__page-tag">Book a Repair</span>
          <h2 className="qf__page-title">Start Your Repair</h2>
          <p className="qf__page-sub">Select your device, choose what needs fixing, and pick a time to come in.</p>
        </div>

        {/* ── Card ── */}
        <div className="qf__card">

          {/* Progress bar */}
          <div className="qf__progress-wrap">
            {visibleSteps.map((label, i) => (
              <div key={label} className={`qf__prog-step ${i < visualStep ? 'done' : ''} ${i === visualStep ? 'active' : ''}`}>
                <div className="qf__prog-dot">
                  {i < visualStep ? <CheckIcon size={12} /> : i + 1}
                </div>
                <span className="qf__prog-label">{label}</span>
              </div>
            ))}
            <div
              className="qf__progress-bar"
              style={{ width: `${(visualStep / (visibleSteps.length - 1)) * 100}%` }}
            />
          </div>

          {/* ── STEP 0: Choose Device ── */}
          {step === STEP_DEVICE && (
            <div className="qf__step" key="device">
              <div className="qf__step-header">
                <h3 className="qf__step-q">What type of device do you have?</h3>
              </div>
              <div className="qf__device-grid">
                {devices.map(d => (
                  <button
                    key={d.id}
                    className="qf__device-card"
                    onClick={() => selectAndAdvanceDevice(d)}
                  >
                    <div className="qf__device-illus">{d.illustration}</div>
                    <span className="qf__device-label">{d.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── STEP 1: Choose Model ── */}
          {step === STEP_MODEL && (
            <div className="qf__step" key="model">
              <div className="qf__step-header">
                <button className="qf__back-link" onClick={() => setStep(STEP_DEVICE)}>
                  ← Back
                </button>
                <h3 className="qf__step-q">Which {deviceId} model do you have?</h3>
                <p className="qf__step-hint">Scroll to find your model</p>
              </div>
              <div className="qf__model-grid">
                {(models[deviceId] || []).map(m => (
                  <button
                    key={m}
                    className={`qf__model-btn ${model === m ? 'qf__model-btn--active' : ''}`}
                    onClick={() => setModel(m)}
                  >
                    {m}
                    {model === m && <span className="qf__model-check"><CheckIcon size={11} /></span>}
                  </button>
                ))}
              </div>
              <div className="qf__footer">
                <span className="qf__footer-hint">{model || 'Select your model'}</span>
                <button
                  className="qf__continue-btn"
                  disabled={!model}
                  onClick={() => setStep(STEP_REPAIR)}
                >
                  Continue <ArrowRight size={15} />
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 2: Choose Repair ── */}
          {step === STEP_REPAIR && (
            <div className="qf__step" key="repair">
              <div className="qf__step-header">
                <button className="qf__back-link" onClick={() => setStep(device?.hasModels ? STEP_MODEL : STEP_DEVICE)}>
                  ← Back
                </button>
                <h3 className="qf__step-q">What needs to be repaired?</h3>
                <p className="qf__step-hint">{displayDevice}</p>
              </div>
              <div className="qf__repair-grid">
                {(repairs[deviceId] || []).map(r => (
                  <button
                    key={r.name}
                    className="qf__repair-card"
                    onClick={() => { setRepair(r.name); setStep(STEP_DATE); }}
                  >
                    <span className="qf__repair-icon">{r.icon}</span>
                    <span className="qf__repair-name">{r.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── STEP 3: Choose Date & Time ── */}
          {step === STEP_DATE && (
            <div className="qf__step" key="date">
              <div className="qf__step-header">
                <button className="qf__back-link" onClick={() => setStep(STEP_REPAIR)}>← Back</button>
                <h3 className="qf__step-q">When would you like to come in?</h3>
                <p className="qf__step-hint">{displayDevice} · {repair}</p>
              </div>

              <div className="qf__date-section">
                <label className="qf__field-label"><CalSvg /> Preferred Date</label>
                <input
                  type="date"
                  className="qf__date-input"
                  min={today}
                  value={date}
                  onChange={e => { setDate(e.target.value); setSelHour(null); setSelMin(null); }}
                />
                {date && <span className="qf__hours-note">{hoursNote}</span>}
              </div>

              {date && (
                <div className="qf__time-section">
                  <label className="qf__field-label">Select Time</label>
                  <div className="qf__drum-wrap">
                    <div className="qf__drum-col">
                      <span className="qf__drum-lbl">Hour</span>
                      <ScrollPicker
                        key={`hr-${date}`}
                        items={availHours}
                        value={selHour}
                        onChange={(h) => { setSelHour(h); setSelMin(null); }}
                        formatItem={fmt12}
                      />
                    </div>
                    <div className="qf__drum-sep">:</div>
                    <div className="qf__drum-col">
                      <span className="qf__drum-lbl">Minute</span>
                      <ScrollPicker
                        key={`min-${selHour}-${date}`}
                        items={availMins}
                        value={selMin}
                        onChange={setSelMin}
                        disabled={selHour === null}
                        formatItem={(m) => String(m).padStart(2, '0')}
                      />
                    </div>
                  </div>

                  {selHour !== null && selMin !== null && (
                    <div className="qf__time-chosen">
                      <span>⏰</span> {fmtTime(selHour, selMin)}
                    </div>
                  )}

                  {needsCallConfirm && (
                    <div className="qf__call-confirm-notice">
                      <PhoneCallIcon size={14} />
                      <span>Please call us to confirm this appointment: <strong>(937) 235-0298</strong></span>
                    </div>
                  )}
                </div>
              )}

              {timeSlot && (
                <div className="qf__contact-section">
                  <div className="qf__contact-row">
                    <div className="qf__contact-field">
                      <label className="qf__field-label">Your Name</label>
                      <input
                        type="text"
                        className="qf__text-input"
                        placeholder="First & Last Name"
                        value={custName}
                        onChange={e => setCustName(e.target.value)}
                      />
                    </div>
                    <div className="qf__contact-field">
                      <label className="qf__field-label">Phone Number</label>
                      <input
                        type="tel"
                        className="qf__text-input"
                        placeholder="(937) 000-0000"
                        value={custPhone}
                        onChange={e => setCustPhone(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="qf__contact-field qf__contact-field--full">
                    <label className="qf__field-label"><EmailSvg /> Email Address <span className="qf__email-badge">Confirmation sent here</span></label>
                    <input
                      type="email"
                      className="qf__text-input"
                      placeholder="you@example.com"
                      value={custEmail}
                      onChange={e => setCustEmail(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="qf__footer">
                <span className="qf__footer-hint">
                  {date && timeSlot ? `${formatDate(date)} at ${timeSlot}` : 'Pick a date and time'}
                </span>
                <button
                  className="qf__continue-btn"
                  disabled={!date || !timeSlot || !custName.trim() || !custPhone.trim() || !custEmail.trim() || sending}
                  onClick={handleConfirm}
                >
                  {sending ? (
                    <><span className="qf__spinner"/> Sending…</>
                  ) : (
                    <>Confirm Appointment <ArrowRight size={15} /></>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 4: Confirmation ── */}
          {step === STEP_DONE && (
            <div className="qf__step qf__confirm" key="done">
              <div className="qf__confirm-top">
                <div className="qf__confirm-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <h3 className="qf__confirm-title">You're All Set!</h3>
                <p className="qf__confirm-sub">We'll see you at Fix'n More — 8239 Troy Pike, Huber Heights, OH</p>
              </div>

              {/* Appointment highlight */}
              <div className="qf__appt-block">
                <div className="qf__appt-icon">📅</div>
                <div className="qf__appt-info">
                  <span className="qf__appt-label">Your Appointment</span>
                  <span className="qf__appt-datetime">{formatDate(date)} at {timeSlot}</span>
                </div>
              </div>

              <div className="qf__summary-card">
                <div className="qf__summary-row">
                  <span>Name</span>
                  <strong>{custName}</strong>
                </div>
                <div className="qf__summary-row">
                  <span>Phone</span>
                  <strong>{custPhone}</strong>
                </div>
                <div className="qf__summary-row">
                  <span>Device</span>
                  <strong>{displayDevice}</strong>
                </div>
                <div className="qf__summary-row">
                  <span>Repair</span>
                  <strong>{repair}</strong>
                </div>
                <div className="qf__summary-row">
                  <span>Date</span>
                  <strong>{formatDate(date)}</strong>
                </div>
                <div className="qf__summary-row">
                  <span>Time</span>
                  <strong>{timeSlot}</strong>
                </div>
                <div className="qf__summary-row">
                  <span>Location</span>
                  <strong>8239 Troy Pike, Huber Heights OH</strong>
                </div>
              </div>

              {emailSent ? (
                <div className="qf__email-confirm-card qf__email-confirm-card--sent">
                  <div className="qf__screenshot-emoji">✉️</div>
                  <div>
                    <h4 className="qf__screenshot-title">Confirmation Email Sent!</h4>
                    <p className="qf__screenshot-text">
                      We sent your appointment details to <strong>{custEmail}</strong>. Check your inbox — and <strong>check your spam folder</strong> just in case.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="qf__email-confirm-card">
                  <div className="qf__screenshot-emoji">📋</div>
                  <div>
                    <h4 className="qf__screenshot-title">Appointment Saved!</h4>
                    <p className="qf__screenshot-text">
                      We'll see you on <strong>{formatDate(date)} at {timeSlot}</strong>. Save or screenshot this page, or call us to confirm.
                    </p>
                  </div>
                </div>
              )}

              <div className="qf__confirm-actions">
                <a href="tel:9372350298" className="qf__call-btn">
                  <PhoneCallIcon size={15} /> Call to Confirm
                </a>
                <button className="qf__restart-btn" onClick={reset}>
                  ↩ Start Over
                </button>
              </div>
            </div>
          )}

        </div>{/* end card */}
      </div>
    </section>
  );
}
